import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { Text } from '@/components/core';
import { AlertFilledIcon } from '@/components/icons';
import { useAgentIcon, useAnnotations } from '@/hooks';
import { Annotation } from '@/utils';

import { Cell, IndentedDiv, Indicator } from './common';
import { EventTooltip, EventTooltipBodyProps } from './EventTooltip';

const IPredictionEventTooltipBody = ({ event }: EventTooltipBodyProps) => {
  const { forEach } = useAnnotations({ predictionId: event.resourceId });

  const isValidated = (annotation: Annotation) => {
    const hasFormatterWarnings = annotation.formatters?.some((f) => f.warnings?.length > 0);
    const hasFormatterErrors = annotation.formatters?.some((f) => f.errors?.length > 0);
    const hasValidatorWarnings = annotation.validators?.some((f) => f.warnings?.length > 0);
    const hasValidatorErrors = annotation.validators?.some((f) => f.errors?.length > 0);
    return !hasFormatterWarnings && !hasFormatterErrors && !hasValidatorWarnings && !hasValidatorErrors;
  };

  const { numValidated, numFlagged } = useMemo(() => {
    let numValidated = 0;
    let numFlagged = 0;

    forEach(({ predictionAnnotation }) => {
      if (predictionAnnotation) {
        if (isValidated(predictionAnnotation)) {
          ++numValidated;
        } else {
          ++numFlagged;
        }
      }
    });

    return { numValidated, numFlagged };
  }, [forEach]);

  const { content } = useMemo(() => {
    let content;
    switch (event.status) {
      case 'succeeded':
        content = (
          <div className="gap-sm flex flex-col">
            {numValidated > 0 ? (
              <div className="gap-sm flex items-center">
                <Indicator variant="success" />
                <div className="flex rounded-xs bg-gray-700 px-1">
                  <Text>{numValidated}</Text>
                </div>
                <Text>values validated by AI</Text>
              </div>
            ) : null}
            {numFlagged > 0 ? (
              <div className="gap-sm flex items-center">
                <Indicator variant="warning" />
                <div className="flex rounded-xs bg-gray-700 px-1">
                  <Text>{numFlagged}</Text>
                </div>
                <Text>values flagged for review</Text>
              </div>
            ) : null}
          </div>
        );
        break;
      case 'failed':
        content = (
          <div className="gap-sm flex flex-col">
            <div className="gap-sm flex items-center">
              <Indicator variant="error" />
              <Text>Failed to extract</Text>
            </div>
            <IndentedDiv>
              <Text>
                Something went wrong while processing the file. Try again or contact support if the issue persists.
              </Text>
            </IndentedDiv>
            {event.warnings?.map((w, i) => (
              <IndentedDiv key={i}>
                <Text>{w}</Text>
              </IndentedDiv>
            ))}
            {event.errors?.map((e, i) => (
              <IndentedDiv key={i}>
                <Text>{e}</Text>
              </IndentedDiv>
            ))}
          </div>
        );
        break;
      case 'running':
      case 'pending':
        content = (
          <div className="gap-sm flex flex-col">
            <Text>The AI agent is extracting data. This may take a few minutes.</Text>
          </div>
        );
        break;
      default:
        content = null;
    }

    return { content };
  }, [event, numFlagged, numValidated]);

  return content;
};

const PredictionEventTooltipBody = memo(IPredictionEventTooltipBody) as typeof IPredictionEventTooltipBody;

export type PredictionEventTooltipProps = {
  event: AgentRunEvent;
  agentId: string;
  runId: string;
};

const IPredictionEventTooltip = ({ event, agentId, runId }: PredictionEventTooltipProps) => {
  const agentIcon = useAgentIcon({
    agentId,
    className: event.status === 'pending' || event.status === 'running' ? 'animate-spin-slowly size-4' : 'size-4',
  });

  const { heading } = useMemo(() => {
    let heading;
    switch (event.status) {
      case 'succeeded':
        heading = 'Extracted by AI agent';
        break;
      case 'failed':
        heading = 'AI agent failed';
        break;
      case 'running':
      case 'pending':
        heading = 'AI agent is working';
        break;
      default:
        heading = 'Unknown prediction status';
    }

    return { heading };
  }, [event]);

  const icon = useMemo(
    () => (
      <Focusable>
        <Cell>
          {event.status === 'failed' ? (
            <div className="relative">
              {agentIcon}
              <AlertFilledIcon className="text-error-500 absolute right-0 bottom-0 z-50 size-3 translate-x-2.5 translate-y-2.5 rounded-full bg-white" />
            </div>
          ) : (
            <>{agentIcon}</>
          )}
        </Cell>
      </Focusable>
    ),
    [agentIcon, event]
  );

  return (
    <EventTooltip
      event={event}
      agentId={agentId}
      runId={runId}
      heading={heading}
      Body={PredictionEventTooltipBody}
      timestamp={event.timestamp}
      icon={icon}
    />
  );
};

export const PredictionEventTooltip = memo(IPredictionEventTooltip) as typeof IPredictionEventTooltip;
